import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, forwardRef, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AdMessageService } from '@internal/common-ui/ad-message';
import { ComponentAPI, PanelComponentModel } from '../../panel-component.model';
import { DialogService } from 'primeng/dynamicdialog';
import { ToolIcon, TreeNodeModel } from './treenode.model';
import _ from 'lodash';


export const ADTreeNode_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => FolderTreeComponent),
  multi: true
}

type SuggestionType = {
  label: string;
  disabled?: boolean;
  data?: any;
}

type FolderTreeApi = ComponentAPI;

export interface FolderTreeEvent {
  node: TreeNodeModel,
  revert: () => void,
}



@Component({
  selector: 'folder-tree',
  templateUrl: './folder-tree.component.html',
  styleUrls: ['./folder-tree.component.scss'],
  providers: [ADTreeNode_VALUE_ACCESSOR, DialogService, AdMessageService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FolderTreeComponent implements OnInit, ControlValueAccessor, PanelComponentModel {
  defaultToolsSets: ToolIcon[] = [
    {
      name: 'edit',
      icon: 'fa fa-pencil',
      onClick: (event: any, node: TreeNodeModel) => {
        console.log(`edit is click for ${node.label}`);
        if (!this.nodeInEdit) {
          this.switchIntoEditMode(node);
        } else {
          this.focusAutoCompleteInput();
        }
      },
      label: 'Rename Group',
    },
    {
      icon: 'fa fa-plus',
      onClick: (event: any, node: TreeNodeModel) => {
        console.log(`create is click for ${node.label}`);
        if (!this.nodeInEdit) {
          let childnode: TreeNodeModel = {
            parent: node,
            expandedIcon: node.expandedIcon,
            collapsedIcon: node.collapsedIcon
          };
          node.children = [...node.children || [], childnode];
          node.expanded = true;
          this.selected = childnode;
          this.switchIntoEditMode(childnode);
          event.stopPropagation();
          event.preventDefault();
        } else {
          this.focusAutoCompleteInput();
        }
      },
      label: 'Create Sub-Folder',
    },
    {
      name: 'delete',
      icon: 'fa fa-trash',
      onClick: (event: any, node: TreeNodeModel) => {
        this.api.safeDelete(node);
      },
      label: 'Delete Group',
    }
  ];

  revertDelete(node: TreeNodeModel, nodeArray: TreeNodeModel[], index: number) {
    nodeArray.splice(index, 0, node);
    this.cd.detectChanges();
  }

  onDeleteNode(node: TreeNodeModel, skipEventEmit?: boolean) {
    let virtalRoot = { children: this.TreeNodes };
    let searchResult = this.findParentOfChild(virtalRoot, node);
    if (searchResult.found) {
      if (searchResult.parent === virtalRoot) {
        var index = this.TreeNodes.findIndex(it => it == node);
        this.TreeNodes = this.TreeNodes.filter(it => it != node);
        var parent = this.TreeNodes;
      } else {
        var index = searchResult.parent.children.findIndex(it => it == node);
        searchResult.parent.children = searchResult.parent.children.filter(it => it != node);
        var parent = <TreeNodeModel[]>searchResult.parent.children;
      }
      if (!skipEventEmit) {
        this.onNodeDelete.emit({ node, revert: () => this.revertDelete(node, parent, index) });
        this.cd.detectChanges();
      }
    }
  }

  findParentOfChild(parentNode: TreeNodeModel, child: TreeNodeModel): { found: boolean, parent?: TreeNodeModel } {
    if (!parentNode.children || !parentNode.children.length) {
      return { found: false };
    } else {
      for (let each of parentNode.children) {
        if (each === child) {
          return { found: true, parent: parentNode };
        } else {
          let searchResult = this.findParentOfChild(each, child);
          if (searchResult.found) {
            return searchResult;
          }
        }
      }
      return { found: false };
    }
  }

  getToolSets(node: TreeNodeModel) {
    if (node.toolsetDisable === false) {
      return [];
    }
    if (node.tools && node.tools.length > 0) {
      return node.tools;
    } else {
      if (this.toolSetsFunc) {
        return this.toolSetsFunc(node, this.nodeToolsets);
      }
      return this.nodeToolsets;
    }
  }
  writeValue(obj: any): void {
    this._selected = obj;
    this.cd.detectChanges();
  }
  registerOnChange(fn: any): void {
    this.onModelChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onModelTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
  onModelChange: Function = () => { };
  onModelTouched: Function = () => { };

  @Input()
  disabled: boolean;

  @Input()
  showDocCount = false;

  @Input()
  toolSetsFunc: (node, toolSets: ToolIcon[]) => ToolIcon[] = null;

  api: FolderTreeApi = {
    insert: (param?: Partial<TreeNodeModel>) => this.insertAtTopLevel(param),
    refresh: () => this.cd.detectChanges(),
    findParent: (child: TreeNodeModel) => this.findParent(child),
    safeDelete: (node: TreeNodeModel) => {
      console.log(`delete is click for ${node.label}`);
      if (!this.nodeInEdit) {
        this.adMessageService.areYouSureToDoSomething(
          `Are you sure to delete ${node.label}`,
          () => this.onDeleteNode(node),
          null,
          true,
          'Yes',
          'No',
          'Warning'
        )
      } else {
        this.focusAutoCompleteInput();
      }
    }
  }

  findParent(child: TreeNodeModel) {
    let queue = [...this.TreeNodes];
    while (queue.length > 0) {
      let assensor = queue.shift();
      if (this.isParent(child, assensor)) {
        return assensor;
      }
      else if (assensor != child && assensor.children != null && assensor.children.length > 0) {
        queue.push(...assensor.children);
      }
    }
    return undefined;
  }

  isParent(child: TreeNodeModel, parent: TreeNodeModel) {
    return parent.children != null && parent.children.indexOf(child) >= 0;
  }

  insertAtTopLevel(param?: Partial<TreeNodeModel>) {
    if (!this.nodeInEdit) {
      let insertedNode: TreeNodeModel = {
        key: '',
        label: '',
        icon: this.TreeNodes[0]?.icon,
        expandedIcon: this.TreeNodes[0]?.expandedIcon,
        collapsedIcon: this.TreeNodes[0]?.collapsedIcon
      }
      Object.assign(insertedNode, param || {});
      this.TreeNodes.unshift(insertedNode);
      this.selected = insertedNode;
      this.cd.detectChanges();
      setTimeout(() => { this.onDoubleClick(null, insertedNode); this.cd.detectChanges(); }, 100);
    } else {
      this.focusAutoCompleteInput();
    }

  }

  _selected: TreeNodeModel | TreeNodeModel[] | null | undefined;
  @Input()
  get selected(): TreeNodeModel | TreeNodeModel[] | null | undefined {
    return this._selected;
  }
  set selected(val: TreeNodeModel | TreeNodeModel[] | null | undefined) {
    if (this.disabled) {
      return;
    }
    this._selected = val;
    this.onModelChange(this._selected);
    if (this.nodeInEdit && this.nodeInEdit != val && val) {
      this.focusAutoCompleteInput();
    }
  }

  nodeInEdit: TreeNodeModel;
  isNodeInEdit(node: TreeNodeModel) {
    return !this.readOnly && this.nodeInEdit != null && this.nodeInEdit === node;
  }
  selectedFolderName: SuggestionType | string | undefined | null;
  suggestions: SuggestionType[] = [];
  filteredSuggestions: SuggestionType[] = [];
  isAutoCompletePanelOpen: boolean;

  onFilterSuggestion({ query }) {
    this.filteredSuggestions = this.suggestions.filter(it => it.label && it.label.toLowerCase().indexOf(query.toLowerCase()) >= 0);
  }

  onkeyup(event) {
    //enter
    if (event.which == 13) {
      this.onConfirmInput(event);
    }
  }

  revertUpsert(node: TreeNodeModel, label: string) {
    if (!label || !label.length) {
      this.onDeleteNode(node, true);
    } else {
      node.label = label;
    }
    this.cd.detectChanges();
  }

  onConfirmInput(event) {
    setTimeout(() => {
      if (this.nodeInEdit && !this.isAutoCompletePanelOpen) {
        let label = (this.selectedFolderName as SuggestionType)?.label;
        if (!label || !label.length) {
          label = this.selectedFolderName as string;
        }
        if (this.validateIfNameNotEmpty(label)) {
          // name is legal
          if (this.validateIfNameLegal(label)) {
            if (this.nodeInEdit.label != label) {
              let [node, originalLabel] = [this.nodeInEdit, this.nodeInEdit.label];
              this.nodeInEdit.label = label;
              this.onUpsert.emit({ node, revert: () => this.revertUpsert(node, originalLabel) });
            }
            this.nodeInEdit = null;
            this.selectedFolderName = null;
            this.cd.detectChanges();
          } else {
            // duplicated name
            this.adMessageService.areYouSureToDoSomething(
              `${label} has already been used, please enter another one`,
              () => { this.resetInput(); },
              null,
              false,
              'Ok',
              null,
              'Warning'
            );
          }
        } else {
          // entered name is empty
          // new folder branch
          if (!this.nodeInEdit.label || !this.nodeInEdit.label.length) {
            this.onDeleteNode(this.nodeInEdit, true);
            this.nodeInEdit = null;
            this.cd.detectChanges();
          } else {
            // edit folder branch
            this.adMessageService.areYouSureToDoSomething(
              `You can't set folder to an empty name`,
              () => { this.resetInput(); },
              null,
              false,
              'Ok',
              null,
              'Warning'
            );
            this.resetInput();
          }
        }
      }
    }, 200);
  }

  resetInput() {
    this.selectedFolderName = { label: this.nodeInEdit.label };
    this.cd.detectChanges();
  }

  validateIfNameNotEmpty(name: string): boolean {
    return name && name.length > 0;
  }

  validateIfNameLegal(name: string) {
    let match = this.suggestions.find(it => it.label.toLowerCase() == name.toLowerCase());
    if (match) {
      return !match.disabled;
    } else {
      return true;
    }
  }

  onSelectInput(option: SuggestionType) {
    if (this.nodeInEdit) {
      if (!option.disabled) {
        let fromSuggestion = this.suggestions.find(it => it.label.toLocaleLowerCase() === option.label);
        if (fromSuggestion != null) {
          this.nodeInEdit.data = fromSuggestion.data;
        }
        if (this.nodeInEdit.label != option.label) {
          let [node, originalLabel] = [this.nodeInEdit, this.nodeInEdit.label];
          this.nodeInEdit.label = option.label;
          this.onUpsert.emit({ node, revert: () => this.revertUpsert(node, originalLabel) });
        }
        this.nodeInEdit = null;
        this.selectedFolderName = null;
        this.cd.detectChanges();
      }

    }
  }

  onNodeClick(event) {
    if (this.disabled) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  onNodeSelect(event) {
    // if(this.data&&this.data.close!=null){
    //     this.data.close();
    // }
    if (this.disabled) {
      this.cd.detectChanges();
    }
  }

  onItemClick(event: any, node: SuggestionType) {
    if (node.disabled) {
      event.stopPropagation();
      event.preventDefault();
      console.log('click stop');
    }
  }
  onDoubleClick(event: any, node: TreeNodeModel) {
    if (node.editable === false) {
      return;
    }
    if (!this.nodeInEdit) {
      this.switchIntoEditMode(node);
    } else {
      if ((event as MouseEvent) != null) {
        event.stopPropagation();
        event.preventDefault();
        console.log('double click stop');
      }
      this.focusAutoCompleteInput();
    }
  }

  switchIntoEditMode(node: TreeNodeModel) {
    this.nodeInEdit = node;
    this.refreshSuggestions();
    this.selectedFolderName = { label: node.label };
    this.focusAutoCompleteInput();
  }

  refreshSuggestions() {
    let namesInTree = this.getFolderNamesInTree();
    this.suggestions = this.legalFolders.map(it => ({
      label: it,
      disabled: namesInTree.indexOf(it) >= 0 && this.nodeInEdit.label != it
    }));
    let nameOnlyInTree = namesInTree
      .filter(it => this.legalFolders.indexOf(it) < 0 && it && it.length)
      .map(it => ({
        label: it,
        disabled: true && this.nodeInEdit.label != it
      }));
    this.suggestions = [...this.suggestions || [], ...nameOnlyInTree || []]
      .sort((a, b) => a.disabled == true ? 1 : -1);
    this.filteredSuggestions = [];
  }

  getFolderNamesInTree() {
    let names = [];
    for (let each of this.TreeNodes) {
      this.getFamilyNames(each, names);
    }
    return names;
  }

  getFamilyNames(node: TreeNodeModel, names: string[]) {
    if (node != null) {
      names.push(node.label);
      for (let child of node.children || []) {
        this.getFamilyNames(child, names);
      }
    }
  }


  ngOnInit(): void {
    //throw new Error('Method not implemented.');
    // if(this.data!=null){
    //     this.readOnly=true;
    //     Object.assign(this,this.data);
    //     this.cd.detectChanges();
    // }
    this.applyDefaultIcon();
    this.isInitialized = true;
  }

  randomAutoCompleteId: string;
  isInitialized: boolean = false;

  constructor(
    public cd: ChangeDetectorRef,
    private adMessageService: AdMessageService
  ) {
    this.randomAutoCompleteId = `${Math.floor(Math.random() * 1000)}-tree`;
  }

  ngAfterViewInit(): void {
    //Called after ngAfterContentInit when the component's view has been initialized. Applies to components only.
    //Add 'implements AfterViewInit' to the class.
    this.onTreeReady.emit(this.api);
  }

  _treeNodes: TreeNodeModel[] = [];
  @Input()
  get TreeNodes() {
    return this._treeNodes;
  }
  set TreeNodes(val: TreeNodeModel[]) {
    this._treeNodes = _.cloneDeep(val || []);
    if (this.isInitialized) {
      this.applyDefaultIcon();
    }
  }

  @Input()
  nodeToolsets: ToolIcon[] = this.defaultToolsSets;

  @Input()
  selectionMode: 'single' | 'multiple' | 'checkbox' = 'single';

  @Input()
  readOnly: boolean = false;

  @Input()
  style: any;

  @Input()
  filter: boolean = false;

  @Input()
  filterBy: string = 'label';

  @Input()
  filterPlaceholder: string = 'Search';

  @Input()
  styleClass: string;

  @Input()
  treeItemLabelStyle: any = { display: 'inline-block', width: '100%' };

  @Input()
  trackBy: Function = (index: number, item: any) => item;

  get treeStyleClass(): string {
    return `${this.styleClass && this.styleClass.length ? this.styleClass : ''} wider-list-label ${this.disabled ? 'ui-disable' : ''}`;
  }

  @Input()
  legalFolders: string[] = [];

  @Input()
  scrollHeight: string;

  @Input()
  ignoreIcon: boolean = false;

  @Input()
  expandedIcon: string | Function = 'fa fa-folder-open-o';

  @Input()
  collapsedIcon: string | Function = 'fa fa-folder-o';

  @Input() forceSelection: boolean;

  @Output()
  onNodeDelete: EventEmitter<FolderTreeEvent> = new EventEmitter<FolderTreeEvent>();

  @Output()
  onUpsert: EventEmitter<FolderTreeEvent> = new EventEmitter<FolderTreeEvent>();

  @Output()
  onTreeReady: EventEmitter<FolderTreeApi> = new EventEmitter<FolderTreeApi>();
  //data:{TreeNodes: TreeNodeModel[],styleClass?:string,close:Function,filter:boolean,style?:any,scrollHeight?:string};

  clickdebug(node) {
    console.log(node.label);
  }

  onmouseenter(node: TreeNodeModel, toollabel: string) {
    if (node.activeTool != toollabel) {
      node.activeTool = toollabel;
    }
  }

  onmouseleave(node: TreeNodeModel, toollabel: string) {
    if (node.activeTool == toollabel) {
      node.activeTool = '';
    }
  }

  focusAutoCompleteInput() {
    setTimeout(() => {
      const autoCompleteInput = document.getElementById(this.randomAutoCompleteId);
      if (autoCompleteInput) {
        autoCompleteInput.focus();
        console.log('focus input');
      }
    }, 100)

  }

  applyDefaultIcon() {
    if (this.gothroughAllTree(this.getIsPartialIconMissingFunction()) === true) {
      return;
    }
    if (this.ignoreIcon) {
      this.gothroughAllTree(this.getIgnoreIconFunction());
    } else {
      this.gothroughAllTree(this.getApplyDefaultIconFunction());
    }
  }

  gothroughAllTree(fn: (node: TreeNodeModel) => boolean | undefined | null | void) {
    let queue = [...this.TreeNodes || []];
    while (queue.length > 0) {
      let node = queue.shift();
      let isExit = fn(node);
      if (isExit === true) {
        return true;
      }
      if (node.children != null && node.children.length > 0) {
        queue.push(...node.children);
      }
    }
  }

  getIgnoreIconFunction() {
    return function (node: TreeNodeModel) {
      node.expandedIcon = null;
      node.collapsedIcon = null;
    }
  }

  getApplyDefaultIconFunction() {
    let [expandedIcon, collapsedIcon] = [this.expandedIcon, this.collapsedIcon];
    return function (node: TreeNodeModel) {
      if (expandedIcon instanceof Function) {
        node.expandedIcon = expandedIcon(node);
      }
      else {
        node.expandedIcon = expandedIcon;
      }
      if (collapsedIcon instanceof Function) {
        node.collapsedIcon = collapsedIcon(node);
      }
      else {
        node.collapsedIcon = collapsedIcon;
      }
    }
  }

  getIsPartialIconMissingFunction() {
    let [hasIcon, hasNotIcon] = [false, false];
    return function (node: TreeNodeModel) {
      if (
        (node.collapsedIcon != null && node.collapsedIcon.length > 0) ||
        (node.expandedIcon != null && node.expandedIcon.length > 0)
      ) {
        hasIcon = true;
      }
      if (
        (!node.collapsedIcon ||
          !node.collapsedIcon.length ||
          !node.expandedIcon ||
          !node.expandedIcon.length)
      ) {
        hasNotIcon = true;
      }
      return hasIcon && hasNotIcon;
    }
  }
}
